<template>
  <div ref="contentPopupContainer" class="content-popup-container" :class="size">
    <div
      ref="contentPopup"
      class="content-popup"
      :class="[
        {
          'is-visible': dataVisible,
          'has-overflow-menu-button': showOverflowMenuButton,
          'has-header-icon': hasHeaderIcon,
        },
      ]"
    >
      <div class="wrapper">
        <div class="content-popup-header">
          <slot name="header-icon" />
          <div class="title" :data-text="title" />
          <slot name="header-icon-right" />
          <cv-link v-if="showCloseButton" class="close" @click.stop="handleClose">
            <svg-icon name="Cross24" kind="action" />
          </cv-link>
        </div>
        <div class="content-popup-content">
          <slot name="content" />
        </div>
        <div
          v-if="hasAction"
          class="content-popup-actions"
          :class="{ 'has-info-logo': showInfoLogo }"
        >
          <slot name="action">
            <cv-button
              v-if="showCancelButton"
              class="cancel"
              kind="tertiary"
              size="small"
              @click="handleCancel"
              @mousedown.prevent
            >
              <span :data-text="computedSecondaryButtonLabel" />
            </cv-button>
            <fetch-data-button
              v-if="showSaveButton"
              class="save"
              :kind="dataSaveButton.kind"
              :size="dataSaveButton.size"
              :error="dataSaveButton.error"
              :loading="dataSaveButton.loading"
              :disabled="disableSave"
              @click="handleSave"
              @reset-error-state="handleResetError"
              @reset-completed-state="handleResetCompleted"
            >
              <span :data-text="computedPrimaryButtonLabel" />
            </fetch-data-button>

            <!-- information icon-->
            <cv-interactive-tooltip v-if="showInfoLogo" class="field-tooltip" direction="bottom">
              <template #trigger>
                <svg-icon name="Info24" kind="action" />
              </template>
              <template #content>
                <p>{{ infoLogoMessage }}</p>
              </template>
            </cv-interactive-tooltip>

            <!-- overflow menu -->
            <overflow-menu-button
              v-if="showOverflowMenuButton"
              class="save"
              kind="primary"
              size="small"
            >
              {{ overflowMenuButtonLabel }}
              <template #overflow-menu>
                <OverflowMenuItem
                  v-for="button in overflowMenuButtonItems"
                  :key="button.label"
                  :disabled="button.disabled"
                  @click="button.action"
                >
                  <svg-icon v-if="button.icon" :name="button.icon" kind="action" />
                  {{ button.label }}
                </OverflowMenuItem>
              </template>
            </overflow-menu-button>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick, useSlots, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { createPopper, Placement } from "@popperjs/core";
import ScrollUtils from "@utils/scroll.utils";
import DOMUtils from "@utils/dom.utils";
import { isNull } from "@utils/var.check";
import { useVm } from "@utils/vm.ts";
import { SaveButton } from "@plugins/common/types.ts";

interface OverflowMenuItem {
  label: string;
  disabled?: boolean;
  action: () => void;
  icon?: string;
}

// Props
/** Popper offset: `[skidding, distance]` or a function receiving the popper rect. */
type PopupOffset =
  [number, number] | ((args: { popper: { width: number; height: number } }) => [number, number]);

interface Props {
  title?: string;
  visible?: boolean;
  placement?: string;
  boundary?: string;
  rootBoundary?: string;
  additionalScrollingOffset?: number;
  referenceElement?: HTMLElement | null;
  showCloseButton?: boolean;
  showCancelButton?: boolean;
  showSaveButton?: boolean;
  disableSaveButton?: boolean;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
  size?: "large" | "small";
  showOverflowMenuButton?: boolean;
  overflowMenuButtonLabel?: string | null;
  overflowMenuButtonItems?: OverflowMenuItem[];
  showInfoLogo?: boolean;
  infoLogoMessage?: string | null;
  autoScroll?: boolean;
  offset?: PopupOffset;
}

// Composables
const { vm } = useVm();
const slots = useSlots();
const { t } = useI18n();

const {
  title = "",
  visible = false,
  placement = "bottom",
  boundary = "",
  rootBoundary = "",
  additionalScrollingOffset = 0,
  referenceElement = null,
  showCloseButton = false,
  showCancelButton = true,
  showSaveButton = true,
  disableSaveButton = false,
  primaryButtonLabel = "",
  secondaryButtonLabel = "",
  size = "large",
  showOverflowMenuButton = false,
  overflowMenuButtonLabel = null,
  overflowMenuButtonItems = [],
  showInfoLogo = false,
  infoLogoMessage = null,
  autoScroll = true,
  offset: popperOffset = undefined,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  onConfirm: [saveButton: SaveButton];
  onClose: [];
  onCancel: [event: Event];
  onResetError: [];
  onResetCompleted: [];
}>();

// Template refs
const contentPopupContainer = useTemplateRef<HTMLElement>("contentPopupContainer");
const contentPopup = useTemplateRef<HTMLElement>("contentPopup");

// Reactive data
const dataVisible = ref(false);
const popperInstance = ref<unknown>(null);
const dataSaveButton = ref<SaveButton>({
  kind: "primary",
  size: "small",
  error: false,
  loading: false,
});
const hasHeaderIcon = ref(false);

// Computed properties
const computedPrimaryButtonLabel = computed(() => {
  return primaryButtonLabel || t("button_save");
});

const computedSecondaryButtonLabel = computed(() => {
  return secondaryButtonLabel || t("button_assignment_cancel");
});

const disableSave = computed(() => {
  return dataSaveButton.value.loading || disableSaveButton;
});

const hasAction = computed(() => {
  return showSaveButton || showCancelButton || showOverflowMenuButton;
});

// Methods
const handleSave = () => {
  emit("onConfirm", dataSaveButton.value);
  DOMUtils.removeClass("body", "content-popup-opened");
};

const handleClose = () => {
  emit("onClose");
  hideContentPopup();
};

const handleCancel = (event: Event) => {
  emit("onCancel", event);
};

const handleResetError = () => {
  emit("onResetError");
};

const handleResetCompleted = () => {
  emit("onResetCompleted");
};

const hideContentPopup = () => {
  dataVisible.value = false;
  DOMUtils.removeClass("body", "content-popup-opened");
};

const showContentPopup = () => {
  dataVisible.value = true;
  DOMUtils.addClass("body", "content-popup-opened");

  if (referenceElement) {
    const boundaryEl = DOMUtils.parent(referenceElement, boundary);
    const rootBoundaryEl = DOMUtils.parent(referenceElement, rootBoundary);
    const container = contentPopupContainer.value;

    if (container) {
      popperInstance.value = createPopper(referenceElement, container, {
        strategy: "absolute",
        placement: placement as Placement,
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: boundaryEl,
              rootBoundary: rootBoundaryEl,
            },
          },
          {
            name: "flip",
            enabled: false,
          },
          // Only applied when an offset is requested, other consumers are unaffected
          ...(popperOffset ? [{ name: "offset", options: { offset: popperOffset } }] : []),
        ],
        onFirstUpdate: () => {
          /* Content subpage doesn't have App header, so scrollToView function can be used
           * which doesn't accept an offset currently
           * In pages w/o content subpage, window.scrolTop is used instead which allows for precise
           * scrolling with smooth animation as scrolling the window in content subpage won't work */
          setTimeout(() => {
            if (!autoScroll) {
              return;
            }

            const contentSubpage = DOMUtils.parent(referenceElement, ".content-subpage");
            const popupEl = contentPopup.value;

            if (!popupEl) return;

            if (contentSubpage) {
              ScrollUtils.scrollToView(popupEl, {
                behavior: "smooth",
                block: "start",
              });
            } else {
              const headerOffset = 50;
              let globalBannerOffset = 0;
              const globalBanners = DOMUtils.elOf(document.documentElement, ".global-banner");

              globalBanners.forEach(() => {
                globalBannerOffset += 40;
              });

              const offset = headerOffset + globalBannerOffset;
              const top =
                popupEl.getBoundingClientRect().top +
                window.pageYOffset -
                offset -
                additionalScrollingOffset;

              ScrollUtils.windowScrollTop(top);
            }
          }, 50);
        },
      });
    }
  }

  // Auto focus when content popup is shown
  nextTick(() => {
    const mceContentBody = DOMUtils.elOf(vm.$el, ".mce-content-body")[0];
    if (mceContentBody) {
      mceContentBody.focus();
    }
  });
};

const checkSlots = () => {
  nextTick(() => {
    hasHeaderIcon.value = !!slots["header-icon"];
  });
};

// Watchers
watch(
  () => visible,
  (val) => {
    if (val) {
      showContentPopup();
    } else {
      hideContentPopup();
    }
  },
);

watch(
  () => referenceElement,
  (newReference) => {
    nextTick(() => {
      if (popperInstance.value) {
        if (isNull(newReference)) {
          // SLSSIT-7946, destroy popperInstance if incoming reference el is empty
          // new popperInstance will reinit when calling show subpage below
          popperInstance.value.destroy();
        } else {
          popperInstance.value.state.elements.reference = newReference;
          popperInstance.value.update();
        }
      }
    });
  },
);

// Lifecycle
onMounted(() => {
  checkSlots();
});
</script>

<style lang="scss">
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;
@use "@styles/abstracts/variables" as *;

// Intentional margin  to give allowance for annotation pop-up to show in the same position as requested by user
.content-popup-opened {
  .page-main {
    margin-bottom: 50vh !important;
  }
}

.page._condensed {
  .content-popup-container {
    @include media("tablet-min") {
      width: 39.5rem;
    }

    @include media("desktop") {
      width: 55rem;
    }
  }
}

.content-popup {
  .rich-text-editor {
    &:has(.mce-edit-focus) {
      .mce-toolbar {
        border: 1px solid rgba($interactive-01, 40%);
        border-bottom-color: $ui-03;
      }
    }

    .tox-tinymce {
      display: flex !important;
    }

    .mce-toolbar {
      border-radius: 10px 10px 0 0;
      // Tinymce toolbar will be present in the DOM, so empty pseudo class is used to check
      &:not(:empty) {
        + .mce-content-body {
          border-radius: 0 0 10px 10px;
        }
      }
    }

    .mce-content-body {
      max-height: 5rem !important;
      border-radius: 10px;

      @include media("desktop") {
        height: 7rem;
        max-height: 7rem !important;
      }
    }
  }

  .rich-text-editor .mce-toolbar .tox-editor-container,
  .rich-text-editor .mce-toolbar .tox-toolbar__primary {
    border: none;
  }
}

.content-subpage {
  .content-popup-container {
    &.large {
      min-width: 400px;
      max-width: 680px;

      @include media("mobile") {
        min-width: unset;
        max-width: calc(100vw - #{$spacing-07});
      }

      @include media("tablet") {
        max-width: calc(#{$content-subpage-tablet-width} - #{$spacing-07});
      }
    }
  }
}

.content-popup-container {
  z-index: z-map("content-popup");
  position: absolute;
  left: 0;
  // To override the inline styling added by Popper.js for right:auto causing the width
  // of the content popup to be incorrect
  right: 0 !important;

  @include media("mobile") {
    max-width: calc(100vw - #{$spacing-07});
  }

  &.learning-fa {
    .content-popup-header {
      .title {
        color: $ai;
      }
    }
  }

  &.small {
    max-width: 343px;
  }

  .content-popup {
    margin-left: auto;
    margin-right: auto;
    padding: 0rem;
    width: 100%;
    display: none;

    &.has-overflow-menu-button {
      // padding-bottom to give space allowance for the overflow-menu for the scenario whereby
      // the document body is short and there is no space below the content popup. Otherwise the user won't
      // be able to select some of the items
      padding-bottom: $layout-06;

      .content-popup-actions {
        &:not(:empty) {
          .bx--tooltip__trigger {
            margin-left: 0;
          }

          > .field-tooltip {
            display: inline-flex;
          }

          &.has-info-logo {
            // Workaround to fix for overflow menu button in viewport less than 375px width,
            // as Samsung S20 model is still being supported with a width of 360px
            @media only screen and (max-width: 375px) {
              display: grid;
              grid-template-rows: 1fr 1fr;
              grid-template-columns: 1fr 7rem 1fr;
              grid-gap: $spacing-03;

              > .bx--btn {
                &:first-child {
                  grid-column: 2;
                }
              }

              > .field-tooltip {
                position: relative;
                left: 3rem;
                grid-row: 2;
              }

              > .overflow-menu-button {
                grid-row: 2;
              }
            }
          }
        }
      }
    }

    &.is-visible {
      display: block;
    }

    &.has-header-icon {
      .content-popup-header {
        justify-content: flex-start;

        > .popup-icon {
          display: flex;
          align-items: center;
          margin-right: $spacing-03;
        }

        > .bx--link {
          margin-left: auto;
        }
      }
    }

    > .wrapper {
      background: $ui-01;
      border-radius: 10px;
      box-shadow: $shadow-02;
      border: 1px solid $ui-03;
    }

    @include media("mobile") {
      max-width: calc(100vw - #{$spacing-07});
      margin-left: 0;
      margin-right: 0;
    }

    @include media("tablet-min") {
      max-width: 39.5rem;
    }

    @include media("desktop") {
      max-width: 55rem;
    }
  }

  .bx--btn {
    min-width: 8rem;
  }

  .bx--text-input__field-wrapper {
    @include media("mobile") {
      &::placeholder {
        font-size: 11px;
      }
    }
  }

  div[data-text],
  span[data-text] {
    &:before {
      content: attr(data-text);
    }
  }

  .content-popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $spacing-05;

    .title {
      @include carbon--type-style("productive-heading-01", true);
      padding-right: $spacing-05;
      word-break: break-word;
    }

    .close {
      display: flex;
      min-width: 40px;
      height: 40px;
      justify-content: center;
      align-items: center;
      border-radius: 100%;

      &:hover {
        background-color: $hover-ui;
      }

      &:focus {
        outline: transparent;
        border: 1px solid $focus;
      }

      .sls-icon {
        [fill] {
          fill: $interactive-01;
        }
      }
    }
  }

  .content-popup-content {
    padding: 0 $spacing-05 $spacing-05;
  }

  .content-popup-actions {
    padding: $spacing-05;

    &:not(:empty) {
      display: flex;
      justify-content: center;
      border-top: 1px solid $ui-background;
      gap: $spacing-05;
    }

    .bx--btn {
      // Override generic min-width to fit buttons in content-popup for 360px Mobile width
      min-width: 8rem;
    }

    .bx--tooltip__trigger {
      &:hover,
      &:active,
      &:focus {
        .sls-icon {
          fill: none;
        }
      }

      [stroke] {
        stroke: $interactive-01;
      }

      .sls-icon {
        path {
          fill: $interactive-01;
        }
      }
    }
  }
}
</style>
